import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Spin, Typography } from 'antd'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  accountLinkedAuthMethodsRetrieveOptions,
  accountLinkedAuthMethodsRetrieveQueryKey,
  accountUserPartialUpdateMutation,
  authorizationV2PasswordRequestCreateMutation
} from 'src/api/main'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { useShowApiErrors } from 'src/utils'

const { Paragraph, Text } = Typography

const resetPasswordRedirectUrl = `${window.location.origin}${paths.auth.resetPasswordConfirm}`

export const ChangePasswordForm = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { user } = useSessionContext()
  const userEmail = user?.user?.email

  const [isFormOpen, setIsFormOpen] = useState(false)

  const [setPasswordForm] = Form.useForm<{
    new_password: string
    confirm_password: string
  }>()

  const [changePasswordForm] = Form.useForm<{
    password: string
    new_password: string
    confirm_password: string
  }>()

  const { data: linkedAuthMethods, isLoading } = useQuery(
    accountLinkedAuthMethodsRetrieveOptions()
  )

  const hasPassword = linkedAuthMethods?.has_password ?? true

  const setPasswordMutation = useMutation({
    ...accountUserPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('account.messages.passwordSet'))
      setPasswordForm.resetFields()
      setIsFormOpen(false)
      await queryClient.invalidateQueries({
        queryKey: accountLinkedAuthMethodsRetrieveQueryKey()
      })
    },
    onError: (e) => showApiErrors(e)
  })

  const changePasswordMutation = useMutation({
    ...accountUserPartialUpdateMutation(),
    onSuccess: () => {
      message.success(t('account.securityCenter.password.passwordChanged'))
      changePasswordForm.resetFields()
      setIsFormOpen(false)
    },
    onError: (e) => showApiErrors(e)
  })

  const { mutateAsync: requestPasswordReset, isPending: isRequestingReset } =
    useMutation({
      ...authorizationV2PasswordRequestCreateMutation(),
      onSuccess: () => {
        message.success(t('account.securityCenter.password.requestSent'))
      },
      onError: (e) => showApiErrors(e)
    })

  const handleSetPassword = () => {
    setPasswordForm.validateFields().then((values) => {
      setPasswordMutation.mutate({
        body: {
          new_password: values.new_password,
          confirm_password: values.confirm_password
        }
      })
    })
  }

  const handleChangePassword = () => {
    changePasswordForm.validateFields().then((values) => {
      changePasswordMutation.mutate({
        body: {
          password: values.password,
          new_password: values.new_password,
          confirm_password: values.confirm_password
        }
      })
    })
  }

  const handleCancel = () => {
    setIsFormOpen(false)
    setPasswordForm.resetFields()
    changePasswordForm.resetFields()
  }

  if (isLoading) {
    return (
      <Flex justify="center" style={{ paddingBlock: 8 }}>
        <Spin size="small" />
      </Flex>
    )
  }

  if (!isFormOpen) {
    return (
      <Flex vertical gap={12}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {hasPassword
            ? t('account.securityCenter.password.changeDescription')
            : t('account.authentication.setPassword.description')}
        </Paragraph>
        <div>
          <Button
            icon={<KeyRound size={16} />}
            onClick={() => setIsFormOpen(true)}
          >
            {hasPassword
              ? t('account.securityCenter.password.changeButton')
              : t('account.authentication.setPassword.submit')}
          </Button>
        </div>
      </Flex>
    )
  }

  if (!hasPassword) {
    return (
      <Flex vertical gap={12}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('account.authentication.setPassword.description')}
        </Paragraph>
        <Form
          form={setPasswordForm}
          layout="vertical"
          requiredMark={false}
          style={{ maxWidth: 400 }}
        >
          <Form.Item
            name="new_password"
            label={t('account.authentication.setPassword.newPassword')}
            rules={[
              {
                required: true,
                message: t(
                  'account.authentication.setPassword.passwordRequired'
                )
              },
              {
                pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+]).{10,}$/,
                message: t('password_policy')
              }
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label={t('account.authentication.setPassword.confirmPassword')}
            dependencies={['new_password']}
            rules={[
              {
                required: true,
                message: t('account.authentication.setPassword.confirmRequired')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error(
                      t('account.authentication.setPassword.passwordsMustMatch')
                    )
                  )
                }
              })
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Flex gap={8}>
              <Button
                type="primary"
                loading={setPasswordMutation.isPending}
                onClick={handleSetPassword}
              >
                {t('account.authentication.setPassword.submit')}
              </Button>
              <Button onClick={handleCancel}>{t('cancel')}</Button>
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={12}>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        {t('account.securityCenter.password.changeDescription')}
      </Paragraph>
      <Form
        form={changePasswordForm}
        layout="vertical"
        requiredMark={false}
        style={{ maxWidth: 400 }}
      >
        <Form.Item
          name="password"
          label={t('account.securityCenter.password.currentPassword')}
          rules={[
            {
              required: true,
              message: t(
                'account.securityCenter.password.currentPasswordRequired'
              )
            }
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="new_password"
          label={t('account.securityCenter.password.newPassword')}
          rules={[
            {
              required: true,
              message: t('account.securityCenter.password.passwordRequired')
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+]).{10,}$/,
              message: t('password_policy')
            }
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label={t('account.securityCenter.password.confirmPassword')}
          dependencies={['new_password']}
          rules={[
            {
              required: true,
              message: t('account.securityCenter.password.confirmRequired')
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error(
                    t('account.securityCenter.password.passwordsMustMatch')
                  )
                )
              }
            })
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Flex justify="space-between" align="center">
            <Flex gap={8}>
              <Button
                type="primary"
                loading={changePasswordMutation.isPending}
                onClick={handleChangePassword}
              >
                {t('account.securityCenter.password.changeButton')}
              </Button>
              <Button onClick={handleCancel}>{t('cancel')}</Button>
            </Flex>
            <Text>
              <Button
                type="link"
                size="small"
                loading={isRequestingReset}
                style={{ padding: 0 }}
                onClick={() =>
                  void requestPasswordReset({
                    body: {
                      username: userEmail ?? '',
                      redirect_url: resetPasswordRedirectUrl
                    }
                  })
                }
              >
                {t('account.securityCenter.password.forgotPassword')}
              </Button>
            </Text>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  )
}
